import { i18n } from "src/boot/i18n"
import { Log } from "src/util/classes/log"

/**
 * Returns an appropriate greeting based on the time of the day.
 * Format is: "Good X, Y!", where Y is the current user name and X is:
 *
 * <ul>
 *   <li>00 - 8:59 - morning</li>
 *   <li>09 - 12:59 - day</li>
 *   <li>13 - 17:59 - afternoon</li>
 *   <li>18 - 23:59 - evening</li>
 * </ul>
 */
export function getGreeting(userEmail?: string) {
  const { t } = i18n.global
  const currentHours = new Date().getHours()
  // Should be username, but we don't currently have that info
  let appropriateGreeting = ""

  if (currentHours >= 0 && currentHours < 9) {
    appropriateGreeting = t("time.good_morning")
  } else if (currentHours >= 9 && currentHours < 13) {
    appropriateGreeting = t("time.good_day")
  } else if (currentHours >= 13 && currentHours < 18) {
    appropriateGreeting = t("time.good_afternoon")
  } else {
    appropriateGreeting = t("time.good_evening")
  }

  userEmail = userEmail?.trim()
  return userEmail !== "" && userEmail !== undefined
    ? `${appropriateGreeting}, ${userEmail}`
    : `${appropriateGreeting}`
}

// Format a given Date as YYYY-MM-DD HH:mm. Can exclude time if needed.
function formatDate(date: Date, withTime: boolean = true) {
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const currentDate = date.getDate().toString().padStart(2, "0")
  const dateFormatted = [year, month, currentDate].join("-")
  if (!withTime) {
    return dateFormatted
  }

  const hours = date.getUTCHours().toString().padStart(2, "0")
  const minutes = date.getUTCMinutes().toString().padStart(2, "0")
  return dateFormatted + " " + [hours, minutes].join(":")
}

// Get the notification message
function getNotificationMsg(minutes: number, hours: number, days: number, sysTimeStamp: string) {
  const { t } = i18n.global
  if (minutes == 1) {
    return minutes + t("time.minute_ago")
  } else if (minutes < 60) {
    return minutes + t("time.minutes_ago")
  } else if (hours == 1) {
    return hours + t("time.hour_ago")
  } else if (hours < 24) {
    return hours + t("time.hours_ago")
  } else if (days == 1) {
    return days + t("time.day_ago")
  } else if (days < 7) {
    return days + t("time.days_ago")
  } else {
    return sysTimeStamp
  }
}

// Get the deadline message
function getDeadlineMsg(days: number, past: boolean, date: Date) {
  const { t } = i18n.global
  if (days <= 0) {
    return t("time.today")
  }

  if (past) {
    if (days == 1) {
      return days + t("time.day_ago")
    } else if (days < 7) {
      return days + t("time.days_ago")
    } else {
      const weeks = Math.floor(days / 7)
      if (weeks <= 8) {
        return t("time.x_weeks_ago", { weeks }, weeks)
      }
      return t("time.was_date", { date: formatDate(date, false) })
    }
  }

  if (days == 1) {
    return t("time.in") + days + t("time.day")
  } else if (days <= 7) {
    return t("time.in") + days + t("time.days")
  } else {
    const weeks = Math.floor(days / 7)
    if (weeks <= 8) {
      return t("time.in_x_weeks", { weeks }, weeks)
    }
    return formatDate(date, false)
  }
}

/**
 * Returns the temporal info in a humanized form when the date and time is closer to the present
 *
 * @param timeStamp In UTC format (ex: 2020-07-16T10:30:01.000Z)
 * @param identifier Location where the temporal info is displayed in the UI. The different options are described below:
 * <ul>
 *   <li><b>card</b> - in cards</li>
 *   <li><b>objOverviewIssue</b> - issues in object overviews</li>
 *   <li><b>objOverviewWorkOrder</b> - work orders in object overviews</li>
 *   <li><b>deadline</b> - for deadline in work order cards</li>
 * </ul>
 *
 * */
export function getTemporalInfo(
  timeStamp: string,
  identifier: "card" | "objOverviewIssue" | "objOverviewWorkOrder" | "deadline" | "date"
) {
  const { t } = i18n.global
  const now = new Date()
  now.setSeconds(0, 0)

  const match = /(\d+)-(\d+)-(\d+)T(\d+):(\d+)/.exec(timeStamp)

  // Shouldn't happen, but just in case
  if (!match) {
    return Number.NaN
  }

  // Remove the unnecessary data
  match.shift()

  const [year, month, day, hour, min] = match.map(Number)

  // Change date string's time zone to UTC and get timestamp:
  const sysTime = Date.UTC(year, month - 1, day, hour, min)

  // Get the system time stamp in YYYY-MM-DD HH:mm format
  const date = new Date(Date.UTC(year, month - 1, day, hour, min))
  const sysTimeStamp = formatDate(date)

  // Get the current time
  const currentDateTime = now.getTime()

  // Time difference in minutes
  const offset = Math.floor((currentDateTime - sysTime) / (1000 * 60))
  const offsetMinutes = Math.abs(offset)
  const offsetHours = Math.floor(Math.abs(offset) / 60)
  const offsetDays = Math.floor(Math.abs(offset) / (60 * 24))

  switch (identifier) {
    case "card":
      return getNotificationMsg(offsetMinutes, offsetHours, offsetDays, sysTimeStamp)
    case "objOverviewIssue":
      return (
        t("time.latest_raised") +
        getNotificationMsg(offsetMinutes, offsetHours, offsetDays, sysTimeStamp)
      )
    case "objOverviewWorkOrder":
      return t("time.deadline") + " " + getDeadlineMsg(offsetDays, offset > 0, date).toLowerCase()
    case "date":
      return sysTimeStamp
    default:
      return getDeadlineMsg(offsetDays, offset > 0, date)
  }
}

/**
 *  Returns the time duration in a human-readable format
 *
 *  If timeEstimate has Days
 *    return X days and Y hours
 *  Else If timeEstimate has Hours
 *    return Y hours and Z minutes
 *  Else If timeEstimate has minutes
 *    return Z minutes
 *  Else
 *    return Less than a minute
 *
 * @param timeEstimate In the format -> Days Hours:Minutes:Seconds
 *                     Ex: "4 13:34:36", "17:04:09", "00:08:05", "00:00:07"
 */
export function getTimeRequirement(timeEstimate: string) {
  const { t } = i18n.global
  let data = timeEstimate.trim()
  let day = 0

  if (data.includes(" ")) {
    const split = data.split(" ")
    day = Number(split[0])
    data = split[1]
  }

  const hoursAndMinutes = data.split(":")
  const hour = Number(hoursAndMinutes[0])
  const minute = Number(hoursAndMinutes[1])

  if (day) {
    return getDaysRequirement(day, hour)
  } else if (hour) {
    return getHoursRequirement(hour, minute)
  } else if (minute) {
    return minute > 1
      ? t("time.minutes_with_placeholder", { minute: minute })
      : t("time.minute_with_placeholder", { minute: minute })
  } else {
    return t("time.less_than_a_minute")
  }
}

function getDaysRequirement(day: number, hour: number) {
  const { t } = i18n.global

  if (day > 1 && hour > 1) {
    return t("time.days_and_hours", { day: day, hour: hour })
  } else if (day > 1) {
    if (hour == 0) return t("time.days_with_placeholder", { day: day })
    return t("time.days_and_hour", { day: day, hour: hour })
  } else if (hour > 1) {
    return t("time.day_and_hours", { day: day, hour: hour })
  } else {
    if (hour == 0) return t("time.day_with_placeholder", { day: day })
    return t("time.day_and_hour", { day: day, hour: hour })
  }
}

function getHoursRequirement(hour: number, minute: number) {
  const { t } = i18n.global

  if (hour > 1 && minute > 1) {
    return t("time.hours_and_minutes", { hour: hour, minute: minute })
  } else if (hour > 1) {
    if (minute == 0) return t("time.hours_with_placeholder", { hour: hour })
    return t("time.hours_and_minute", { hour: hour, minute: minute })
  } else if (minute > 1) {
    return t("time.hour_and_minutes", { hour: hour, minute: minute })
  } else {
    if (minute == 0) return t("time.hour_with_placeholder", { hour: hour })
    return t("time.hour_and_minute", { hour: hour, minute: minute })
  }
}

export function getHourMinuteRequirement(hour: number, minute: number) {
  const { t } = i18n.global

  if (hour > 1 && minute > 1) {
    return t("time.hours_minutes", { hour: hour, minute: minute })
  } else if (hour > 1) {
    if (minute !== 1) return t("time.hours_with_placeholder", { hour: hour })
    return t("time.hours_minute", { hour: hour })
  } else if (minute > 1) {
    if (hour === 1) return t("time.hour_minutes", { minute: minute })
    return t("time.minutes_with_placeholder", { minute: minute })
  } else if (minute === 1) {
    if (hour === 1) return t("time.hour_minute")
    return t("time.minute_with_placeholder", { minute: minute })
  } else {
    if (hour === 1) return t("time.hour_with_placeholder", { hour: hour })
    return t("time.less_than_a_minute")
  }
}

/**
 * Returns a date as a string value in ISO format.
 *
 * @param date ex: "2021-03-25"
 * @param time ex: "10:59"
 * @return string | null ex: "2021-03-25T10:59:00.000Z"
 */
export function getISOTimeString(date: string | undefined, time: string | undefined) {
  if (!date || !time) return null

  const timeStamp = new Date(date + "T" + time)

  if (Number.isNaN(timeStamp.getTime())) {
    Log.error({
      message: "The provide date time strings are not in correct format",
      data: { date: date, time: time },
    })
    return null
  }

  return timeStamp.toISOString()
}
